import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Box, IconButton, Chip, TextField, Button, Typography, Collapse } from '@mui/material'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt'
import { useTheme } from '@mui/material/styles'
import posthog from 'posthog-js'
import { setMessageFeedback, submitMessageFeedback } from 'src/redux/aiCandidateSearch'

const FEEDBACK_REASONS = [
  { key: 'didnt_understand', label: "Didn't understand me" },
  { key: 'wrong_specialty', label: 'Wrong specialty' },
  { key: 'wrong_location', label: 'Wrong location' },
  { key: 'wrong_experience', label: 'Wrong experience level' },
  { key: 'irrelevant_candidates', label: 'Irrelevant candidates' },
  { key: 'missing_criteria', label: 'Missing key criteria' },
  { key: 'inaccurate', label: 'Inaccurate information' },
  { key: 'not_helpful', label: 'Not helpful' },
  { key: 'other', label: 'Other' },
]

const POSITIVE_FEEDBACK_REASONS = [
  { key: 'relevant_candidates', label: 'Relevant candidates' },
  { key: 'right_specialty', label: 'Right specialty' },
  { key: 'good_location', label: 'Good location match' },
  { key: 'right_experience', label: 'Right experience level' },
  { key: 'understood_needs', label: 'Understood my needs' },
  { key: 'helpful_response', label: 'Helpful response' },
  { key: 'other', label: 'Other' },
]

const CandidateMessageFeedback = ({ messageId, chatbotMessageId, feedback, conversationId }) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const [selectedReasons, setSelectedReasons] = useState(feedback?.reasons || [])
  const [comment, setComment] = useState(feedback?.comment || '')
  const [showThanks, setShowThanks] = useState(false)

  const thanksTimerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (thanksTimerRef.current) {
        clearTimeout(thanksTimerRef.current)
      }
    }
  }, [])

  const vote = feedback?.vote || null
  const isSubmitted = !!feedback?.submittedAt
  const canSubmit = selectedReasons.length > 0 || comment.trim().length > 0

  const handleThumbUp = () => {
    if (vote === 'up') {
      dispatch(setMessageFeedback({ messageId, vote: null }))
    } else {
      posthog.capture('employer_candidate_ai_message_feedback_voted', { vote: 'up', context: 'candidates' })
      dispatch(setMessageFeedback({ messageId, vote: 'up', reasons: [], comment: '' }))
      submitMessageFeedback({ chatbotMessageId, conversationId, vote: 'up', reasons: [], comment: '' })
      setSelectedReasons([])
      setComment('')
    }
  }

  const handleThumbDown = () => {
    if (vote === 'down') {
      dispatch(setMessageFeedback({ messageId, vote: null }))
      setSelectedReasons([])
      setComment('')
    } else {
      posthog.capture('employer_candidate_ai_message_feedback_voted', { vote: 'down', context: 'candidates' })
      dispatch(setMessageFeedback({ messageId, vote: 'down', reasons: [], comment: '' }))
      submitMessageFeedback({ chatbotMessageId, conversationId, vote: 'down', reasons: [], comment: '' })
    }
  }

  const handleReasonToggle = (key) => {
    setSelectedReasons(prev =>
      prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]
    )
  }

  const handleSubmit = () => {
    posthog.capture('employer_candidate_ai_message_feedback_submitted', {
      vote,
      reason_count: selectedReasons.length,
      has_comment: comment.trim().length > 0,
      context: 'candidates',
    })
    dispatch(setMessageFeedback({
      messageId,
      vote,
      reasons: selectedReasons,
      comment,
      submittedAt: new Date().toISOString(),
    }))

    submitMessageFeedback({
      chatbotMessageId,
      conversationId,
      vote,
      reasons: selectedReasons,
      comment,
    })

    setShowThanks(true)
    thanksTimerRef.current = setTimeout(() => {
      setShowThanks(false)
    }, 2000)
  }

  const reasonsList = vote === 'up' ? POSITIVE_FEEDBACK_REASONS : FEEDBACK_REASONS
  const reasonsLabel = vote === 'up' ? 'What did you like?' : 'What went wrong?'

  return (
    <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={handleThumbUp}
          sx={{
            minWidth: { xs: 44, md: 'auto' },
            minHeight: { xs: 44, md: 'auto' },
            color: vote === 'up'
              ? theme.palette.ai.main
              : (vote === 'down' ? theme.palette.grey[300] : theme.palette.grey[400]),
            '&:hover': {
              color: vote === 'up' ? theme.palette.ai.main : theme.palette.grey[600],
            },
          }}
        >
          {vote === 'up'
            ? <ThumbUpIcon sx={{ fontSize: { xs: '1.125rem', md: '0.875rem' } }} />
            : <ThumbUpOffAltIcon sx={{ fontSize: { xs: '1.125rem', md: '0.875rem' } }} />
          }
        </IconButton>
        <IconButton
          size="small"
          onClick={handleThumbDown}
          sx={{
            minWidth: { xs: 44, md: 'auto' },
            minHeight: { xs: 44, md: 'auto' },
            color: vote === 'down'
              ? theme.palette.ai.main
              : (vote === 'up' ? theme.palette.grey[300] : theme.palette.grey[400]),
            '&:hover': {
              color: vote === 'down' ? theme.palette.ai.main : theme.palette.grey[600],
            },
          }}
        >
          {vote === 'down'
            ? <ThumbDownIcon sx={{ fontSize: { xs: '1.125rem', md: '0.875rem' } }} />
            : <ThumbDownOffAltIcon sx={{ fontSize: { xs: '1.125rem', md: '0.875rem' } }} />
          }
        </IconButton>
      </Box>

      <Collapse in={!!vote && !isSubmitted} timeout={200}>
        <Box
          sx={{
            mt: 0.5,
            p: { xs: 1.5, md: 1.5 },
            maxWidth: { xs: '100%', md: '80%' },
            backgroundColor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 1.5,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.palette.grey[700], fontStyle: 'normal' }}>
            {reasonsLabel}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.75, md: 0.5 }, mt: 1 }}>
            {reasonsList.map(reason => (
              <Chip
                key={reason.key}
                label={reason.label}
                size="small"
                variant="outlined"
                onClick={() => handleReasonToggle(reason.key)}
                sx={{
                  cursor: 'pointer',
                  height: { xs: 32, md: 24 },
                  fontSize: { xs: '0.8125rem', md: '0.75rem' },
                  borderColor: selectedReasons.includes(reason.key)
                    ? theme.palette.ai.main
                    : theme.palette.grey[300],
                  backgroundColor: selectedReasons.includes(reason.key)
                    ? theme.palette.ai.light
                    : 'transparent',
                  color: selectedReasons.includes(reason.key)
                    ? theme.palette.ai.dark
                    : theme.palette.grey[700],
                  fontWeight: selectedReasons.includes(reason.key) ? 500 : 400,
                  '&:hover': {
                    borderColor: theme.palette.ai.main,
                    backgroundColor: `${theme.palette.ai.light}40`,
                    color: theme.palette.ai.dark,
                  },
                }}
              />
            ))}
          </Box>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            size="small"
            variant="outlined"
            placeholder="Tell us more (optional)..."
            value={comment}
            onChange={e => setComment(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.ai.main,
                },
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              size="small"
              variant="text"
              onClick={handleSubmit}
              disabled={!canSubmit}
              sx={{
                color: theme.palette.ai.main,
                textTransform: 'none',
                minHeight: { xs: 44, md: 'auto' },
                px: { xs: 2, md: 1 },
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Collapse>

      {showThanks && (
        <Typography variant="caption" sx={{ color: theme.palette.grey[500], mt: 0.5, fontStyle: 'normal' }}>
          Thanks for your feedback
        </Typography>
      )}
    </Box>
  )
}

export default CandidateMessageFeedback
